import { useMemo, useState, type ReactNode } from "react";
import type { SortDescriptor } from "react-aria-components";
import { Table } from "@/components/application/table/table";

/**
 * DataTable<Row> — generický sloupcový wrapper NAD oficiálním UUI Table
 * (`@/components/application/table`, react-aria Collection).
 *
 * Konzumeři používají jednoduché `Column<Row>` API (header/cell/sortValue),
 * uvnitř se renderuje skrz oficiální `Table`/`Table.Header`/`Table.Head`/
 * `Table.Body`/`Table.Row`/`Table.Cell` (sort UI, hover, a11y, focus z react-aria).
 *
 * Zachováno z původní logiky:
 *  - client-side sort přes compareSortable (Date/number/cs-CZ localeCompare numeric,
 *    null na konec) — react-aria default sort je stringový, proto řadíme sami a jen
 *    řídíme `sortDescriptor`,
 *  - cyklus asc → desc → none (řízeno přes onSortChange),
 *  - onRowClick → react-aria `onRowAction` (klik i Enter/Space; klik na interaktivní
 *    prvek v buňce row-action NEspustí),
 *  - loading/empty stav přes renderEmptyState.
 */

export type SortDirection = "asc" | "desc";

export interface Column<Row> {
    key: string;
    header: ReactNode;
    cell: (row: Row) => ReactNode;
    className?: string;
    /**
     * Opt-in client-side řazení. Když je definováno, hlavička je řaditelná a cykluje
     * mezi asc → desc → none. Vrácená hodnota se porovnává: čísla numericky, Date
     * podle času, stringy lokalizovaně (cs-CZ), null/undefined řadí na konec.
     */
    sortValue?: (row: Row) => string | number | Date | null | undefined;
}

export interface DataTableProps<Row> {
    caption: string;
    /** Když true, caption se zobrazí i vizuálně nad tabulkou (jinak jen aria-label). */
    captionVisible?: boolean;
    columns: Column<Row>[];
    rows: Row[];
    rowKey: (row: Row) => string;
    emptyMessage?: ReactNode;
    onRowClick?: (row: Row) => void;
    loading?: boolean;
    /** Initial sort (column key + direction). Změna se ovládá uvnitř komponenty. */
    defaultSort?: { columnKey: string; direction: SortDirection };
}

interface SortState {
    columnKey: string;
    direction: SortDirection;
}

function compareSortable(
    a: string | number | Date | null | undefined,
    b: string | number | Date | null | undefined,
): number {
    // null/undefined řadí na konec bez ohledu na směr (UX standard sortovatelných tabulek).
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), "cs-CZ", { numeric: true });
}

export function DataTable<Row>({
    caption,
    captionVisible = false,
    columns,
    rows,
    rowKey,
    emptyMessage = "Žádné záznamy.",
    onRowClick,
    loading,
    defaultSort,
}: DataTableProps<Row>) {
    const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

    const sortedRows = useMemo(() => {
        if (!sort) return rows;
        const col = columns.find((c) => c.key === sort.columnKey);
        if (!col?.sortValue) return rows;
        const accessor = col.sortValue;
        const dir = sort.direction === "asc" ? 1 : -1;
        // Stable sort: tagujeme indexem pro tie-break.
        return rows
            .map((row, i) => ({ row, i, key: accessor(row) }))
            .sort((a, b) => {
                const cmp = compareSortable(a.key, b.key);
                return cmp !== 0 ? dir * cmp : a.i - b.i;
            })
            .map((entry) => entry.row);
    }, [rows, columns, sort]);

    const sortDescriptor: SortDescriptor | undefined = sort
        ? { column: sort.columnKey, direction: sort.direction === "asc" ? "ascending" : "descending" }
        : undefined;

    function handleSortChange(desc: SortDescriptor): void {
        const columnKey = String(desc.column);
        // Zachováváme 3-stavový cyklus asc → desc → none (react-aria default je 2-stavový).
        setSort((prev) => {
            if (!prev || prev.columnKey !== columnKey) return { columnKey, direction: "asc" };
            if (prev.direction === "asc") return { columnKey, direction: "desc" };
            return null;
        });
    }

    const racColumns = useMemo(
        () => columns.map((c, i) => ({ id: c.key, isRowHeader: i === 0, column: c })),
        [columns],
    );

    const rowItems = useMemo(
        () => sortedRows.map((r) => ({ id: rowKey(r), data: r })),
        [sortedRows, rowKey],
    );
    const rowById = useMemo(
        () => new Map(rowItems.map((it) => [it.id, it.data] as const)),
        [rowItems],
    );

    return (
        <div className="flex flex-col gap-2">
            {captionVisible && <p className="text-sm font-semibold text-secondary">{caption}</p>}
            <Table
                aria-label={caption}
                selectionMode="none"
                sortDescriptor={sortDescriptor}
                onSortChange={handleSortChange}
                onRowAction={
                    onRowClick
                        ? (key) => {
                              const r = rowById.get(String(key));
                              if (r) onRowClick(r);
                          }
                        : undefined
                }
                aria-busy={loading || undefined}
                className="rounded-xl ring-1 ring-secondary"
            >
                <Table.Header columns={racColumns}>
                    {(col) => (
                        <Table.Head
                            id={col.id}
                            isRowHeader={col.isRowHeader}
                            allowsSorting={!!col.column.sortValue}
                            className={col.column.className}
                        >
                            {col.column.header}
                        </Table.Head>
                    )}
                </Table.Header>
                <Table.Body
                    items={rowItems}
                    renderEmptyState={() => (
                        <div className="px-6 py-6 text-center text-sm text-tertiary">
                            {loading ? "Načítám…" : emptyMessage}
                        </div>
                    )}
                >
                    {(item) => (
                        <Table.Row id={item.id} columns={racColumns}>
                            {(col) => (
                                <Table.Cell className={col.column.className}>
                                    {col.column.cell(item.data)}
                                </Table.Cell>
                            )}
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </div>
    );
}
